import { InfoCircleOutlined, UserOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import MDEditor from '@uiw/react-md-editor';
import { Avatar, Col, Form, Input, Row, Space, Switch, Typography } from 'antd';
import { FC, useEffect } from 'react';
import { FormProvider, get, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useProgramStepContext } from 'src/features/programsV2/contexts/ProgramStepContext';
import { useCreateOrUpdateProgram } from 'src/features/programsV2/hooks/useCreateOrUpdateProgram';
import { useInstructor } from 'src/features/programsV2/hooks/useInstructor';
import {
  VisualStepForm,
  visualStepSchema,
} from 'src/features/programsV2/schemas/visualStepSchema';
import { CreateOrUpdateProgramPayload } from 'src/features/programsV2/types/program';
import { instructorImageUrlFactory } from 'src/features/programsV2/utils/imageUrlFactory';
import {
  showErrorModal,
  showSuccessModal,
} from 'src/features/programsV2/utils/modalHelpers';
import { getVisualProgramPayload } from 'src/features/programsV2/utils/steps/VisualStep/getVisualProgramPayload';

import { ProgramFormItem } from '../../ProgramFormItem';
import { ProgramSectionItem } from '../../ProgramSectionItem';
import { ProgramStepFooter } from '../../ProgramStepFooter';
import { ProgramSectionGroup } from '../../ui/ProgramSectionGroup';
import { ProgramThumbs } from './ProgramThumbs';

interface VisualStepProps {
  programId?: number;
}

const { Text } = Typography;

export const VisualStep: FC<VisualStepProps> = ({ programId }) => {
  const customEditorComponents = {
    a: ({ href, children, ...props }: any) => (
      <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        style={{ color: 'ciano', textDecoration: 'underline' }}
        {...props}
      >
        {children}
      </a>
    ),
  };

  const navigate = useNavigate();
  const {
    getStepDefaultValues,
    isLoadingProgram,
    isPublishedProgram,
    program,
    programApiInitialValues,
    stepValuesRef,
    shouldShowStepErrors,
  } = useProgramStepContext();

  const { createOrUpdateProgram, isLoading } =
    useCreateOrUpdateProgram(programId);

  const selectedInstructorId = program?.instructors?.[0]?.id;
  const { data: instructor } = useInstructor(selectedInstructorId);

  const selectedGender =
    getStepDefaultValues('configurationStep')?.gender ??
    programApiInitialValues?.configurationStep?.gender;

  const isRecommendQuiz =
    getStepDefaultValues('configurationStep')?.isRecommendQuiz ??
    programApiInitialValues?.configurationStep?.isRecommendQuiz;

  const form = useForm({
    resolver: yupResolver(visualStepSchema),
    defaultValues:
      getStepDefaultValues('visualStep') || programApiInitialValues?.visualStep,
    context: { gender: selectedGender, isRecommendQuiz },
    mode: 'onChange',
  });

  const {
    control,
    formState: { errors },
  } = form;

  const howItWorksDescriptionError = get(errors, 'howItWorks.description');

  const updatedValues = form.watch();
  const isFormValid = form.formState.isValid;

  const handleUpdateProgram = async (
    payload: Partial<CreateOrUpdateProgramPayload>
  ) => {
    try {
      await createOrUpdateProgram(payload);
      showSuccessModal({
        title: 'Sucesso',
        content: 'Dados do programa atualizados com sucesso!',
      });
    } catch (error) {
      showErrorModal({
        title: 'Tivemos um problema...',
        content:
          'Não foi possível atualizar os dados do programa, tente novamente.',
      });
    }
  };

  const onSubmit = async (fields: VisualStepForm) => {
    const payload = getVisualProgramPayload(fields);
    programId && handleUpdateProgram(payload);
  };

  useEffect(() => {
    if (instructor) {
      form.setValue('whoHelpYou.description', instructor.description, {
        shouldValidate: true,
      });
    }
  }, [instructor, form]);

  useEffect(() => {
    if (!selectedInstructorId) {
      form.setValue('whoHelpYou.activated', false, { shouldValidate: true });
    }
  }, [selectedInstructorId]);

  useEffect(() => {
    if (stepValuesRef.current && isPublishedProgram)
      stepValuesRef.current['visualStep'] = {
        isValid: isFormValid,
        values: updatedValues,
        formattedValues: getVisualProgramPayload(updatedValues),
      };
  }, [updatedValues, isFormValid, stepValuesRef]);

  useEffect(() => {
    if (shouldShowStepErrors && !isLoadingProgram) form.trigger();
  }, [form, shouldShowStepErrors, isLoadingProgram]);

  return (
    <FormProvider {...form}>
      <Form layout='vertical' onFinish={form.handleSubmit(onSubmit)}>
        <ProgramSectionItem
          title='Thumb'
          sectionTitle='Design'
          addonAfter={<Text>Use apenas arquivos .png</Text>}
          style={{ marginBottom: '16px' }}
        >
          <ProgramThumbs
            title='Web'
            helperText='.png 1125x634 pixels'
            fieldName='thumb.web'
            maxDimensions={{ width: 1125, height: 634 }}
          />
          <ProgramThumbs
            title='Mobile'
            helperText='.png 591x840 pixels'
            fieldName='thumb.mobile'
            maxDimensions={{ width: 591, height: 840 }}
          />
          <ProgramThumbs
            title='TV'
            helperText='.png 880x496 pixels'
            fieldName='thumb.tv'
            maxDimensions={{ width: 880, height: 496 }}
          />
        </ProgramSectionItem>
        <ProgramSectionItem title='Teaser' style={{ marginBottom: '16px' }}>
          <ProgramSectionGroup>
            <div style={{ width: '100%', position: 'relative' }}>
              <ProgramFormItem
                control={control}
                name='teaser'
                label='Caminho vídeo Spalla (Opcional)'
                renderItem={({ field }) => (
                  <Input
                    value={field.value ? field.value : undefined}
                    onChange={e => field.onChange(e.target.value)}
                    placeholder='Ex: 018c1c7b-396c-7VC6-97f6-0bcXXa067286'
                  />
                )}
              />
            </div>
          </ProgramSectionGroup>
        </ProgramSectionItem>
        <ProgramSectionItem
          title='Logotipo'
          addonAfter={<Text>Use apenas arquivos .png</Text>}
          style={{ marginBottom: '16px' }}
        >
          <ProgramThumbs
            title='Logotipo'
            helperText='.png com largura aprox. 550px e altura ajustada ao conteúdo (sem respiro)'
            fieldName='logotipo'
            maxDimensions={{ width: 550, height: 550 }}
          />
        </ProgramSectionItem>
        <ProgramSectionItem
          title='Banner interno (Detalhes de programa)'
          addonAfter={<Text>Use apenas arquivos .png</Text>}
          style={{ marginBottom: '16px' }}
        >
          <ProgramThumbs
            title='Web'
            helperText='.png 1920x512 pixels'
            fieldName='bannerInterno.web'
            maxDimensions={{ width: 1920, height: 512 }}
          />
          <ProgramThumbs
            title='TV'
            helperText='.png 3840x2160 pixels'
            fieldName='bannerInterno.tv'
            maxDimensions={{ width: 3840, height: 2160 }}
          />
          <ProgramThumbs
            title='Mobile/App'
            helperText='.png 375x200 pixels'
            fieldName='bannerInterno.mobileApp'
            maxDimensions={{ width: 375, height: 200 }}
          />
          <ProgramThumbs
            title='Tablet'
            helperText='.png 834x298 pixels'
            fieldName='bannerInterno.tablet'
            maxDimensions={{ width: 834, height: 298 }}
          />
        </ProgramSectionItem>
        <ProgramSectionItem
          title='Banner quiz'
          addonAfter={<Text>Use apenas arquivos .png</Text>}
          style={{ marginBottom: '16px' }}
        >
          <ProgramThumbs
            title='Web'
            helperText='.png 2520x768 pixels'
            fieldName='bannerQuiz.web'
            maxDimensions={{ width: 2520, height: 768 }}
          />
          <ProgramThumbs
            title='App/responsivo'
            helperText='.png 1968x1320 pixels'
            fieldName='bannerQuiz.appResponsive'
            maxDimensions={{ width: 1968, height: 1320 }}
          />
        </ProgramSectionItem>
        <ProgramSectionItem
          sectionTitle='Abas'
          isEditableTitle
          editableTitleProps={{
            control,
            name: `howItWorks.name`,
            placeholder: 'Novo módulo',
            disabled: !form.watch('howItWorks.activated'),
          }}
          addonAfter={
            <div style={{ display: 'flex', gap: '10px' }}>
              <Text
                style={{
                  fontSize: '14px',
                  display: 'flex',
                  whiteSpace: 'nowrap',
                }}
              >
                Desativar aba
              </Text>
              <Switch
                checked={form.watch('howItWorks.activated')}
                onChange={checked =>
                  form.setValue('howItWorks.activated', checked, {
                    shouldValidate: true,
                  })
                }
              />
            </div>
          }
          style={{ marginBottom: '16px' }}
        >
          <ProgramSectionGroup>
            <Col span={24}>
              <StyledMDEditor data-color-mode='dark'>
                <div className='wmde-markdown-var'></div>
                <MDEditor
                  previewOptions={{
                    components: customEditorComponents,
                  }}
                  value={form.watch('howItWorks.description')}
                  onChange={value =>
                    form.setValue('howItWorks.description', value || '', {
                      shouldValidate: true,
                    })
                  }
                  height={400}
                />
              </StyledMDEditor>
            </Col>
            {howItWorksDescriptionError && (
              <Text type='danger' style={{ paddingTop: '20px' }}>
                {howItWorksDescriptionError.message}
              </Text>
            )}
          </ProgramSectionGroup>
        </ProgramSectionItem>

        <ProgramSectionItem
          isEditableTitle
          editableTitleProps={{
            control,
            name: `whoHelpYou.name`,
            placeholder: 'Novo módulo',
            disabled: !form.watch('whoHelpYou.activated'),
          }}
          addonAfter={
            <div style={{ display: 'flex', gap: '10px' }}>
              <Text
                style={{
                  fontSize: '14px',
                  display: 'flex',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                Desativar aba
              </Text>
              <Switch
                disabled={!selectedInstructorId}
                checked={form.watch('whoHelpYou.activated')}
                onChange={checked =>
                  form.setValue('whoHelpYou.activated', checked)
                }
              />
            </div>
          }
          style={{ marginBottom: '16px' }}
        >
          <ProgramSectionGroup>
            <Text
              type='secondary'
              style={{
                color: '#FFFFFFD9',
                position: 'relative',
                paddingTop: '0px',
                paddingBottom: '12px',
                fontSize: '14px',
                gap: '8px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <InfoCircleOutlined />
              Sempre será a última aba
            </Text>
            <Row
              align={'middle'}
              style={{
                width: '100%',
                border: '1px solid #30363D',
                padding: '16px',
                borderRadius: '8px',
              }}
            >
              <Avatar
                size={72}
                icon={<UserOutlined />}
                src={
                  selectedInstructorId &&
                  instructorImageUrlFactory(selectedInstructorId)
                }
              />

              <Col span={23}>
                <ProgramSectionGroup>
                  <Space
                    direction='vertical'
                    size={4}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <Text
                      type='secondary'
                      style={{
                        color: '#FFFFFFD9',
                        position: 'relative',
                      }}
                    >
                      Treinador
                    </Text>
                    <Input
                      placeholder='Treinador não definido'
                      value={instructor?.name}
                      disabled
                    />
                  </Space>
                </ProgramSectionGroup>
              </Col>
            </Row>
          </ProgramSectionGroup>
        </ProgramSectionItem>

        <ProgramStepFooter
          programId={programId}
          removeNextButton={isPublishedProgram}
          onCancelConfirmation={() => navigate('/programs/v2')}
          isNextButtonDisabled={!form.formState.isValid}
          nextButtonText={programId ? 'Salvar' : 'Próximo'}
          isDisabledCancelButton={isLoading}
          isLoadingNextButton={isLoading}
          style={{
            left: 0,
            right: 0,
            bottom: 0,
            position: 'fixed',
          }}
        />
      </Form>
    </FormProvider>
  );
};

const StyledMDEditor = styled.div`
  .w-md-editor {
    background-color: #141414 !important;
    color: #ffffff !important;
  }

  .w-md-editor-preview {
    background-color: #141414 !important;
    color: #e0e0e0 !important;
  }

  .w-md-editor-content {
    background-color: #141414 !important;
    color: #ffffff !important;
  }

  .w-md-editor-toolbar {
    background-color: #202020 !important;
    color: #fff !important;
  }
`;
